import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { isLimitError } from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { storage } from '../product/productStorage';
import { useProducts } from '../product/ProductContext';
import {
    CREATE_KINDS, TYPE_STYLES, addDays, formatLocalDate, getWeekStart, kindForContentType, parseLocalDate, shortChannel, shortName,
} from '../product/productUtils';
import { loadReportEntries, upsertPlanPostEntry } from '../utils/reportEntries';
import { CopyButton } from './create/generatorUi';
import IdeasPanel, { BestTimesHint } from './IdeasPanel';
import RepurposeMenu from '../components/RepurposeMenu';
import PublishModal from '../components/PublishModal';
import PublishStatus from '../components/PublishStatus';
import { needsPolling, normalizeJob, normalizeJobs } from '../utils/publishing';
import { browserTimezone, chooseSlot, findWinners, formatDayOfWeek, listMetrics, normalizeBestTimes } from '../utils/planInsights';
import './plan.css';
import './ideas.css';

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const UNSCHEDULED = 'Unscheduled';
const METRICS = [
    { id: 'views', label: 'Views' },
    { id: 'likes', label: 'Likes' },
    { id: 'clicks', label: 'Link clicks' },
];
const platformOptions = ['TikTok', 'Instagram', 'YouTube', 'X', 'Facebook', 'LinkedIn'];
const contentTypeOptions = ['Images', 'Videos', 'Carousels', 'Text Posts', 'Stories', 'Blog articles', 'Ads'];
const postsPerWeekOptions = [1, 2, 3, 4, 5, 6, 7, 10, 14];
const VIEWS = [
    { id: 'platform', label: 'By platform' },
    { id: 'day', label: 'By day' },
];
const OTHER_PLATFORM = 'Other';
// Placeholder answers that mean the AI didn't name a platform
const UNKNOWN_PLATFORMS = ['unknown', 'none', 'n/a', 'na', 'other', 'any', 'tbd', '-'];

const DEFAULT_PREFERENCES = {
    targetAudience: '',
    contentGoals: '',
    postsPerWeek: '3',
    platforms: [],
    contentTypes: [],
    toneOfVoice: '',
    industryFocus: '',
    linkDestination: '',
};

// What each automation does, in one line. Ids match product.automation on the server.
const AUTOMATIONS = [
    { id: 'autoPlan', label: 'Plan next week for me', description: 'Every Sunday morning, using your current plan settings.' },
    { id: 'autoDraft', label: 'Draft every post', description: 'Hooks, caption and hashtags ready to pick for each planned post.' },
    { id: 'autoReview', label: 'Review last week', description: 'Monday morning: adjusts the rest of the plan from your results.' },
];

// Starting points for the plan settings; a preset only fills the fields, which stay editable
const PRESETS = [
    {
        id: 'steady',
        label: 'Steady',
        postsPerWeek: '3',
        contentTypes: ['Images', 'Videos', 'Carousels', 'Text Posts'],
        goal: (product) => toText(product?.goal).trim() || 'Grow a loyal audience with steady, useful posts',
    },
    {
        id: 'launch',
        label: 'Launch week',
        postsPerWeek: '7',
        contentTypes: ['Videos', 'Carousels', 'Ads'],
        goal: () => 'Get sign-ups for the launch: a post every day, mostly videos and carousels, with one ad',
    },
    {
        id: 'promo',
        label: 'Promo push',
        postsPerWeek: '5',
        contentTypes: ['Images', 'Videos', 'Carousels', 'Ads'],
        goal: (product) => {
            const offer = toText(product?.offer).trim();
            return `Drive sales of ${offer ? `the offer: ${offer}` : 'the current offer'}. Every post leads to it`;
        },
    },
    {
        id: 'brand',
        label: 'Brand building',
        postsPerWeek: '4',
        contentTypes: ['Carousels', 'Text Posts', 'Videos', 'Blog articles'],
        goal: (product) => `Build awareness${product?.name ? ` of ${product.name}` : ''} with educational posts and the story behind it`,
    },
];

// What a post (hook, caption and hashtags) can hold on each platform; the server caps captions the same way
const CAPTION_LIMITS = {
    x: 280, twitter: 280, bluesky: 300, threads: 500, pinterest: 500, linkedin: 3000, instagram: 2200, tiktok: 2200,
    youtube: 5000, facebook: 5000, reddit: 5000, blog: 5000,
};
const DEFAULT_CAPTION_LIMIT = 2200;
const MAX_HASHTAGS = 15;

const DRAFT_SAVE_DELAY_MS = 400;
const JOBS_POLL_MS = 20 * 1000;
const CAPTION_SAVE_DELAY_MS = 1500;

const REQUIRED_FIELDS = [
    { id: 'targetAudience', label: 'Target audience' },
    { id: 'contentGoals', label: 'Content goals' },
    { id: 'platforms', label: 'Platforms' },
    { id: 'contentTypes', label: 'Content types' },
];

// ---------- Defensive parsing: plans come from the AI, preferences from storage ----------
const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const toText = (value) => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const toCount = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? Math.round(number) : 0;
};

// "monday", "Mon" and "Monday " all mean Monday; anything else is unscheduled
const toDay = (value) => {
    const text = toText(value).trim().toLowerCase();
    return (text.length >= 3 && WEEK_DAYS.find(day => day.toLowerCase().startsWith(text.slice(0, 3)))) || UNSCHEDULED;
};

// "#Meal prep" and "mealprep" are both #mealprep
const toHashtag = (value) => {
    const text = toText(value).trim().replace(/^#+/, '').replace(/[\s#]+/g, '');
    return text ? `#${text}` : '';
};

const uniqueHashtags = (tags) => tags.filter((tag, index) => (
    tag && tags.findIndex(other => other.toLowerCase() === tag.toLowerCase()) === index
));

// The AI's draft of a post; anything that isn't one means the post has no draft
const normalizeDraft = (raw) => {
    if (!isObject(raw)) return null;
    const toString = (value) => (typeof value === 'string' ? value : '');
    const hooks = [...new Set((Array.isArray(raw.hooks) ? raw.hooks : []).map(hook => toString(hook).trim()).filter(Boolean))].slice(0, 3);
    const hook = toString(raw.hook).trim();
    const caption = toString(raw.caption);
    if (!hooks.length && !hook && !caption.trim()) return null;
    return {
        hooks,
        hook: hook || hooks[0] || '',
        caption,
        hashtags: uniqueHashtags((Array.isArray(raw.hashtags) ? raw.hashtags : []).map(tag => toHashtag(toString(tag)))).slice(0, MAX_HASHTAGS),
        auto: raw.auto === true,
        createdAt: toString(raw.createdAt),
        updatedAt: toString(raw.updatedAt),
    };
};

const normalizePost = (post, index) => ({
    ...post,
    id: toText(post.id) || `post-${index}`,
    day: toDay(post.day),
    time: toText(post.time),
    platform: toText(post.platform),
    contentType: toText(post.contentType),
    theme: toText(post.theme),
    hook: toText(post.hook),
    idea: toText(post.idea),
    why: toText(post.why),
    icpId: toText(post.icpId),
    targets: METRICS.reduce((acc, metric) => ({ ...acc, [metric.id]: toCount(post.targets?.[metric.id]) }), {}),
    status: toText(post.status) || 'planned',
    linkSlug: toText(post.linkSlug),
    shortUrl: toText(post.shortUrl),
    clicks: toCount(post.clicks),
    draft: normalizeDraft(post.draft),
    // Where the publishing service posted it: [{ platform, url, id, jobId, at }]
    published: (Array.isArray(post.published) ? post.published : [])
        .filter(item => isObject(item) && isHttpUrl(toText(item.url)))
        .map(item => ({ platform: toText(item.platform), url: toText(item.url), id: toText(item.id), jobId: toText(item.jobId), at: toText(item.at) })),
});

const normalizeReview = (review) => ({
    at: review.at,
    summary: toText(review.summary),
    learnings: (Array.isArray(review.learnings) ? review.learnings : []).map(toText).filter(Boolean),
    changes: toText(review.changes),
});

// Returns null for anything that isn't a plan, otherwise a plan whose fields are safe to render
const normalizePlan = (raw) => {
    if (!isObject(raw)) return null;
    return {
        ...raw,
        auto: raw.auto === true,
        posts: (Array.isArray(raw.posts) ? raw.posts : []).filter(isObject).map(normalizePost),
        reviews: (Array.isArray(raw.reviews) ? raw.reviews : []).filter(isObject).map(normalizeReview),
    };
};

const isHttpUrl = (value) => {
    try {
        return ['http:', 'https:'].includes(new URL(value).protocol);
    } catch (e) {
        return false;
    }
};

const formatWeekRange = (weekStart) => {
    const start = parseLocalDate(weekStart);
    const end = parseLocalDate(addDays(weekStart, 6));
    const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endLabel = end.toLocaleDateString('en-US', start.getMonth() === end.getMonth() ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
    return `${startLabel} – ${endLabel}, ${end.getFullYear()}`;
};

const formatDateTime = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const dayDate = (weekStart, day) => addDays(weekStart, Math.max(0, WEEK_DAYS.indexOf(day)));

// "instagram" and "Instagram" are one section; blanks and placeholders go under Other
const platformName = (platform) => {
    const text = platform.trim();
    if (!text || UNKNOWN_PLATFORMS.includes(text.toLowerCase())) return OTHER_PLATFORM;
    return platformOptions.find(option => option.toLowerCase() === text.toLowerCase()) || text;
};

// Minutes after midnight for "6:00 PM", "18:00" or "9am"; times we can't read sort last
const timeMinutes = (time) => {
    const match = /(\d{1,2})(?::(\d{2}))?\s*([ap])?\.?m?\b/i.exec(time);
    if (!match) return 24 * 60;
    const hours = Number(match[1]) % 12 + (match[3] ? (match[3].toLowerCase() === 'p' ? 12 : 0) : Number(match[1]) >= 12 ? 12 : 0);
    return Math.min(24 * 60, hours * 60 + Number(match[2] || 0));
};

const dayOrder = (day) => (day === UNSCHEDULED ? WEEK_DAYS.length : WEEK_DAYS.indexOf(day));

// Posts sorted by day, then time; the sort is stable, so equal posts keep the plan's order
const byDayAndTime = (a, b) => (dayOrder(a.day) - dayOrder(b.day)) || (timeMinutes(a.time) - timeMinutes(b.time));

// Until the user saves their own settings, start from the product profile
const productDefaults = (product, icps) => {
    if (!product) return {};
    const channels = (product.channels || []).map(channel => toText(channel?.name));
    return {
        targetAudience: icps.length ? icps.map(icp => (icp.descriptor ? `${icp.name} (${icp.descriptor})` : icp.name)).join('; ') : product.targetCustomer || '',
        contentGoals: product.goal || '',
        platforms: platformOptions.filter(option => channels.some(name => name.toLowerCase().startsWith(option.toLowerCase()))),
        toneOfVoice: product.voice?.summary || '',
        industryFocus: product.category || '',
    };
};

const loadPreferences = (product, icps) => {
    const saved = storage.getJson('planPreferences', {});
    const merged = { ...DEFAULT_PREFERENCES, ...productDefaults(product, icps), ...(isObject(saved) ? saved : {}) };
    // Saved settings may be from an older version: keep the shapes the form expects
    return Object.keys(DEFAULT_PREFERENCES).reduce((acc, key) => ({
        ...acc,
        [key]: Array.isArray(DEFAULT_PREFERENCES[key])
            ? (Array.isArray(merged[key]) ? merged[key].filter(item => typeof item === 'string') : [])
            : toText(merged[key]),
    }), {});
};

// The platforms the product is on, for presets; else the ones already picked
const mainPlatforms = (product, current) => {
    const names = [...(product?.channels || []).map(channel => toText(channel?.name)), ...(Array.isArray(product?.platforms) ? product.platforms : []).map(toText)]
        .map(name => name.trim().toLowerCase()).filter(Boolean);
    const platforms = platformOptions.filter(option => names.some(name => name.startsWith(option.toLowerCase())));
    if (platforms.length) return platforms;
    return current.length ? current : ['Instagram', 'TikTok'];
};

const presetMatches = (preset, preferences, product) => (
    preferences.postsPerWeek === preset.postsPerWeek
    && preferences.contentGoals === preset.goal(product)
    && preferences.contentTypes.length === preset.contentTypes.length
    && preset.contentTypes.every(type => preferences.contentTypes.includes(type))
);

const readAutomation = (product) => AUTOMATIONS.reduce((acc, item) => ({ ...acc, [item.id]: product?.automation?.[item.id] === true }), {});

const without = (object, key) => {
    const { [key]: removed, ...rest } = object;
    return rest;
};

// The post as it goes out: the caption follows the hook, then the hashtags
const draftText = (draft) => [draft.hook, draft.caption.trim(), draft.hashtags.join(' ')].filter(Boolean).join('\n\n');

// What "Copy post" copies: the post and the tracked link
const draftCopyText = (draft, shortUrl) => [draftText(draft), shortUrl].filter(Boolean).join('\n\n');

// "YouTube Shorts" counts as YouTube, "Blog articles" as blog
const captionLimit = (platform) => {
    const name = platformName(platform).toLowerCase();
    const key = Object.keys(CAPTION_LIMITS).find(item => name === item || name.startsWith(`${item} `));
    return key ? CAPTION_LIMITS[key] : DEFAULT_CAPTION_LIMIT;
};

// Edited after the AI wrote it, so a rewrite would replace someone's work
const isEditedDraft = (draft) => Boolean(draft.updatedAt && draft.createdAt && draft.updatedAt !== draft.createdAt);

// Post ids come from the AI; ids in the page only take letters, digits, - and _
const domId = (value) => value.replace(/[^\w-]/g, '_');

const isEmptyField = (preferences, field) => (
    Array.isArray(preferences[field]) ? preferences[field].length === 0 : !preferences[field].trim()
);

const PlanScreen = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentProduct, icps: productIcps, replaceProduct, updateProduct } = useProducts();
    const icps = useMemo(() => (Array.isArray(productIcps) ? productIcps : []), [productIcps]);
    // ?week=next opens next week, e.g. from the dashboard's "Plan next week";
    // ?week=YYYY-MM-DD opens that week, e.g. "Back to plan" from a Create screen
    const [weekStart, setWeekStart] = useState(() => {
        const week = searchParams.get('week');
        if (week === 'next') return addDays(getWeekStart(), 7);
        if (/^\d{4}-\d{2}-\d{2}$/.test(week || '') && !Number.isNaN(parseLocalDate(week).getTime())) return getWeekStart(parseLocalDate(week));
        return getWeekStart();
    });
    const [plan, setPlan] = useState(null);
    const [planPreferences, setPlanPreferences] = useState(() => loadPreferences(currentProduct, icps));
    const [showSettings, setShowSettings] = useState(false);
    const [reportEntries, setReportEntries] = useState(loadReportEntries);
    const [busy, setBusy] = useState('loading'); // 'loading' | 'generating' | 'reviewing' | post id being published
    const [error, setError] = useState(null); // { message, severity }
    const [loadFailed, setLoadFailed] = useState(false);
    const [postError, setPostError] = useState(null); // { postId, message }
    const [triedGenerate, setTriedGenerate] = useState(false);
    const [linkTouched, setLinkTouched] = useState(false);
    const [copiedPostId, setCopiedPostId] = useState(null);
    const [view, setView] = useState(() => (storage.get('planView') === 'day' ? 'day' : 'platform'));
    const [showAutomations, setShowAutomations] = useState(() => storage.get('planAutomationsOpen') === 'true');
    const [automationOverrides, setAutomationOverrides] = useState({}); // switches flipped on screen, not yet saved
    const [automationStatus, setAutomationStatus] = useState({ state: 'idle' }); // idle | saving | saved | error (+ retry)
    const [drafting, setDrafting] = useState({}); // { 'week/postId': true } while the AI writes a draft
    const [draftStatus, setDraftStatus] = useState({}); // { 'week/postId': '' | 'saving' | 'saved' | 'error' }
    const [hashtagInputs, setHashtagInputs] = useState({}); // hashtag being typed, per post
    const [hookInputs, setHookInputs] = useState({}); // own hook being typed, per post
    const [bestTimes, setBestTimes] = useState(null); // from tracked link clicks; null until loaded (or if it fails)
    const [followUpBusy, setFollowUpBusy] = useState(''); // id of the winning post getting a follow-up
    const [postNotice, setPostNotice] = useState(null); // { postId, message, weekStart? } after a follow-up is added
    const [jobs, setJobs] = useState([]); // publish jobs for the week on screen
    const [jobBusy, setJobBusy] = useState({}); // { jobId: 'cancel' | 'retry' }
    const [publishPost, setPublishPost] = useState(null); // the post whose Publish modal is open
    const loadId = useRef(0);
    const copiedTimer = useRef(null);
    const automationSeq = useRef({});
    const automationSaving = useRef({ count: 0, failed: null });
    // Draft edits wait here briefly and go out together: { 'week/postId': { weekStart, postId, changes, timer, inFlight } }
    const draftSaves = useRef({});

    useEffect(() => {
        storage.setJson('planPreferences', planPreferences);
    }, [planPreferences]);

    useEffect(() => () => clearTimeout(copiedTimer.current), []);

    // One request per post at a time; edits made meanwhile follow once it's done
    const flushDraft = useCallback(async (key) => {
        const entry = draftSaves.current[key];
        if (!entry) return;
        clearTimeout(entry.timer);
        if (entry.inFlight || !Object.keys(entry.changes).length) return;
        const { changes } = entry;
        entry.changes = {};
        entry.inFlight = true;
        setDraftStatus(previous => ({ ...previous, [key]: 'saving' }));
        try {
            await api.updatePlanPost(entry.weekStart, entry.postId, { draft: changes });
            entry.inFlight = false;
            if (Object.keys(entry.changes).length) {
                flushDraft(key);
                return;
            }
            if (draftSaves.current[key] === entry) delete draftSaves.current[key];
            setDraftStatus(previous => ({ ...previous, [key]: 'saved' }));
        } catch (e) {
            entry.inFlight = false;
            // Kept on screen and retried with whatever was typed since
            entry.changes = { ...changes, ...entry.changes };
            setDraftStatus(previous => ({ ...previous, [key]: 'error' }));
        }
    }, []);

    // Edits not yet sent go out when the page closes
    useEffect(() => {
        const saves = draftSaves.current;
        return () => Object.keys(saves).forEach(key => flushDraft(key));
    }, [flushDraft]);

    const loadPlan = useCallback(async () => {
        // Only the latest week's answer counts when the user pages through weeks quickly
        const id = ++loadId.current;
        setBusy('loading');
        setLoadFailed(false);
        setError(null);
        setPostError(null);
        setDraftStatus({});
        // The week's skeleton shows instead of the previous week's plan
        setPlan(null);
        setJobs([]);
        // Publish status arrives with the plan, so the cards don't grow a line after they show.
        // Without it (an older server, a failed request) the cards just have no status.
        const jobsRequest = api.listPublishJobs({ planWeek: weekStart, limit: 100 }).then(normalizeJobs).catch(() => []);
        try {
            const loaded = normalizePlan(await api.getPlan(weekStart));
            const loadedJobs = await jobsRequest;
            if (id !== loadId.current) return;
            setPlan(loaded);
            setJobs(loadedJobs);
            // Past weeks can't get a new plan, so there's nothing to set up
            setShowSettings(!loaded && weekStart >= getWeekStart());
        } catch (e) {
            if (id !== loadId.current) return;
            setLoadFailed(true);
        } finally {
            if (id === loadId.current) setBusy('');
        }
    }, [weekStart]);

    useEffect(() => {
        loadPlan();
    }, [loadPlan]);

    // Best times are a hint: without them, new posts use the plan's usual times
    useEffect(() => {
        let cancelled = false;
        api.getBestTimes(browserTimezone() || undefined)
            .then(data => !cancelled && setBestTimes(normalizeBestTimes(data)))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [currentProduct?.id]);

    const posts = useMemo(() => plan?.posts || [], [plan]);

    const entryByPostId = useMemo(() => reportEntries.reduce((acc, entry) => (
        entry?.planPostId ? { ...acc, [entry.planPostId]: entry } : acc
    ), {}), [reportEntries]);

    // Views and likes come from the Report tab, clicks from the post's tracked link
    const getActual = useCallback((post) => {
        if (post.status !== 'posted') return null;
        const entry = entryByPostId[post.id];
        return {
            views: entry ? Number(entry.views) || 0 : null,
            likes: entry ? Number(entry.likes) || 0 : null,
            clicks: post.linkSlug ? post.clicks || 0 : null,
        };
    }, [entryByPostId]);

    const sumTotals = useCallback((list) => METRICS.reduce((acc, metric) => ({
        ...acc,
        [metric.id]: {
            target: list.reduce((sum, post) => sum + post.targets[metric.id], 0),
            actual: list.reduce((sum, post) => sum + (getActual(post)?.[metric.id] || 0), 0),
        },
    }), {}), [getActual]);

    const totals = useMemo(() => sumTotals(posts), [posts, sumTotals]);

    // { postId: ['views', ...] }: posted posts that reached a target
    const winners = useMemo(() => findWinners(posts, getActual, plan?.winners), [posts, getActual, plan]);

    // One section per platform: most posts first, then by name; Other always last
    const platformGroups = useMemo(() => {
        const groups = posts.reduce((acc, post) => {
            const name = platformName(post.platform);
            const key = name.toLowerCase();
            const group = acc.find(item => item.key === key);
            if (group) group.posts.push(post);
            else acc.push({ key, name, posts: [post] });
            return acc;
        }, []);
        return groups
            .map(group => ({ ...group, posts: [...group.posts].sort(byDayAndTime), totals: sumTotals(group.posts) }))
            .sort((a, b) => ((a.name === OTHER_PLATFORM) - (b.name === OTHER_PLATFORM))
                || (b.posts.length - a.posts.length)
                || a.name.localeCompare(b.name));
    }, [posts, sumTotals]);

    const missingFields = REQUIRED_FIELDS.filter(field => isEmptyField(planPreferences, field.id)).map(field => field.id);
    const linkInvalid = Boolean(planPreferences.linkDestination.trim()) && !isHttpUrl(planPreferences.linkDestination.trim());
    const fieldError = (field) => triedGenerate && missingFields.includes(field);

    const focusField = (field) => {
        // After the settings card renders
        window.requestAnimationFrame(() => {
            const element = document.getElementById(`plan-${field}`);
            const target = element?.matches('input, select, textarea') ? element : element?.querySelector('input');
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target?.focus({ preventScroll: true });
        });
    };

    const handleGeneratePlan = async () => {
        // Weeks that are over keep their plan as it was
        if (weekStart < getWeekStart()) return;
        setTriedGenerate(true);
        setLinkTouched(true);
        if (missingFields.length > 0 || linkInvalid) {
            setShowSettings(true);
            focusField(missingFields[0] || 'linkDestination');
            return;
        }
        const posted = posts.filter(post => post.status === 'posted').length;
        if (plan && !window.confirm(
            `Replace this week's plan?${posted ? ` The ${posted} posted ${posted === 1 ? 'post is' : 'posts are'} kept.` : ''}`
        )) {
            return;
        }

        // A plan that arrives after the user moved to another week belongs to the week it was made for
        const id = loadId.current;
        setBusy('generating');
        setError(null);
        setPostError(null);
        try {
            const generated = normalizePlan(await api.generatePlan(weekStart, planPreferences));
            if (id !== loadId.current) return;
            if (!generated) throw new Error('Empty plan');
            setPlan(generated);
            setShowSettings(false);
            setTriedGenerate(false);
            if (!generated.posts.length) {
                setError({ severity: 'warning', message: "The AI didn't plan any posts this time. Check the plan settings and try again." });
            }
            // The server marks the first plan as done for the product's setup
            if (currentProduct && !currentProduct.setupDone?.plan) {
                replaceProduct({ ...currentProduct, setupDone: { ...currentProduct.setupDone, plan: true } });
            }
        } catch (e) {
            if (isLimitError(e) || id !== loadId.current) return;
            setError({ severity: 'error', message: "Couldn't generate a plan. Your settings are saved, so please try again in a moment." });
        } finally {
            if (id === loadId.current) setBusy('');
        }
    };

    const handlePublish = async (post) => {
        setPostError(null);
        const destination = planPreferences.linkDestination.trim();
        if (destination && !isHttpUrl(destination)) {
            setLinkTouched(true);
            setPostError({ postId: post.id, message: 'The link destination in plan settings needs a full URL, starting with https://' });
            return;
        }
        setBusy(post.id);
        try {
            const updated = normalizePlan(await api.publishPlanPost(weekStart, post.id, destination || undefined));
            if (!updated) throw new Error('Empty plan');
            setPlan(previous => keepUnsavedDrafts(previous, updated));
            const published = updated.posts.find(p => p.id === post.id) || { ...post, status: 'posted' };
            setReportEntries(upsertPlanPostEntry(published, dayDate(weekStart, post.day), {
                linkSlug: published.linkSlug,
            }));
            if (published.shortUrl) handleCopy(published, { quiet: true });
        } catch (e) {
            setPostError({ postId: post.id, message: toText(e.response?.data?.error) || "Couldn't mark the post as posted. Please try again." });
        } finally {
            setBusy('');
        }
    };

    // Adds a planned post (from an idea or a winner) without the AI. The week on
    // screen shows it at once; other weeks keep it for when they're opened.
    const addPost = async (targetWeek, fields) => {
        const id = loadId.current;
        const updated = normalizePlan(await api.addPlanPost(targetWeek, fields));
        if (!updated) throw new Error('Empty plan');
        if (targetWeek === weekStart && id === loadId.current) {
            setPlan(previous => keepUnsavedDrafts(previous, updated));
            setShowSettings(false);
        }
        return updated;
    };

    // "Do another like this": the next free day this week, else next week
    const handleFollowUp = async (post) => {
        const metrics = winners[post.id] || [];
        const thisWeek = getWeekStart();
        setFollowUpBusy(post.id);
        setPostError(null);
        setPostNotice(null);
        try {
            const weekPosts = async (week) => (week === weekStart ? posts : (await api.getPlan(week))?.posts || []);
            let target = thisWeek;
            let slot = chooseSlot({ weekStart: thisWeek, posts: await weekPosts(thisWeek), platform: post.platform, bestTimes, strict: true });
            if (!slot) {
                target = addDays(thisWeek, 7);
                slot = chooseSlot({ weekStart: target, posts: await weekPosts(target), platform: post.platform, bestTimes, today: target });
            }
            const theme = post.theme || post.hook || 'this post';
            await addPost(target, {
                title: `Follow-up: ${theme}`,
                angle: `Another take on "${theme}", which beat its ${listMetrics(metrics)} target: same format and angle, a fresh example.`,
                why: `Follow-up to a post that beat its ${listMetrics(metrics)} target`,
                platform: post.platform,
                contentType: post.contentType,
                day: slot.day,
                time: slot.time,
                origin: 'winner',
            });
            setPostNotice({
                postId: post.id,
                weekStart: target === weekStart ? '' : target,
                message: `Follow-up planned for ${formatDayOfWeek(target, slot.day)}${slot.time ? `, ${slot.time}` : ''}${target === thisWeek ? '' : ' (next week)'}.`,
            });
        } catch (e) {
            setPostError({ postId: post.id, message: toText(e.response?.data?.error) || "Couldn't plan a follow-up. Please try again." });
        } finally {
            setFollowUpBusy('');
        }
    };

    const makeAd = (post) => navigate('/dashboard/ads', {
        state: { seed: { topic: post.idea || post.theme, hook: post.draft?.hook || post.hook, platform: post.platform, from: 'From a winning post' } },
    });

    const handleResultChange = (post, field, value) => {
        const number = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
        setReportEntries(upsertPlanPostEntry(post, dayDate(weekStart, post.day), {
            [field]: number,
        }));
    };

    const handleReview = async () => {
        const actuals = posts.reduce((acc, post) => {
            const actual = getActual(post);
            return actual ? { ...acc, [post.id]: { views: actual.views, likes: actual.likes } } : acc;
        }, {});
        setBusy('reviewing');
        setError(null);
        try {
            const reviewed = normalizePlan(await api.reviewPlan(weekStart, actuals, formatLocalDate(new Date())));
            if (!reviewed) throw new Error('Empty plan');
            setPlan(previous => keepUnsavedDrafts(previous, reviewed));
        } catch (e) {
            if (isLimitError(e)) return;
            setError({ severity: 'error', message: toText(e.response?.data?.error) || "Couldn't check results. Please try again." });
        } finally {
            setBusy('');
        }
    };

    const handleCopy = async (post, { quiet = false } = {}) => {
        try {
            await navigator.clipboard.writeText(post.shortUrl);
            setCopiedPostId(post.id);
            clearTimeout(copiedTimer.current);
            copiedTimer.current = setTimeout(() => setCopiedPostId(null), 2000);
        } catch (e) {
            if (!quiet) setPostError({ postId: post.id, message: "Couldn't copy the link. Select it and copy it by hand." });
        }
    };

    const draftKey = (postId) => `${weekStart}/${postId}`;

    // A plan from the server keeps the drafts the user is still editing here
    const keepUnsavedDrafts = (previous, next) => {
        if (!previous) return next;
        return {
            ...next,
            posts: next.posts.map(post => {
                const local = draftSaves.current[draftKey(post.id)] && previous.posts.find(item => item.id === post.id);
                return local?.draft ? { ...post, draft: local.draft } : post;
            }),
        };
    };

    // Publish jobs again; when one has just gone out, the plan too (the server marks the post posted)
    const refreshJobs = async () => {
        const id = loadId.current;
        try {
            const next = normalizeJobs(await api.listPublishJobs({ planWeek: weekStart, limit: 100 }));
            if (id !== loadId.current) return;
            const newlyPublished = next.some(job => job.status === 'published'
                && !jobs.some(old => old.id === job.id && old.status === 'published'));
            setJobs(next);
            if (newlyPublished) {
                const updated = normalizePlan(await api.getPlan(weekStart));
                if (updated && id === loadId.current) setPlan(previous => keepUnsavedDrafts(previous, updated));
            }
        } catch (e) {
            // Status stays as it was; the next poll or action tries again
        }
    };

    const refreshJobsRef = useRef(refreshJobs);
    refreshJobsRef.current = refreshJobs;
    const shouldPoll = needsPolling(jobs);

    // Every 20 seconds while something is publishing or about to, and the tab is visible
    useEffect(() => {
        if (!shouldPoll) return undefined;
        const timer = setInterval(() => {
            if (document.visibilityState !== 'hidden') refreshJobsRef.current();
        }, JOBS_POLL_MS);
        return () => clearInterval(timer);
    }, [shouldPoll]);

    const handleJobAction = async (post, job, action) => {
        setJobBusy(previous => ({ ...previous, [job.id]: action }));
        setPostError(null);
        try {
            const updated = action === 'cancel' ? await api.cancelPublishJob(job.id) : await api.retryPublishJob(job.id);
            if (updated && typeof updated === 'object') {
                const next = normalizeJob(updated);
                setJobs(previous => previous.map(item => (item.id === next.id ? next : item)));
            }
            refreshJobs();
        } catch (e) {
            setPostError({
                postId: post.id,
                message: toText(e.response?.data?.error)
                    || (action === 'cancel' ? "Couldn't cancel the scheduled post. Please try again." : "Couldn't retry. Please try again in a moment."),
            });
        } finally {
            setJobBusy(previous => without(previous, job.id));
        }
    };

    const handlePublished = (post, created, { warnings = [] } = {}) => {
        setPublishPost(null);
        setPostNotice(warnings.length ? { postId: post.id, severity: 'warning', message: warnings.join(' ') } : null);
        // Shown at once, then replaced by the server's list
        if (created.length) setJobs(previous => [...created, ...previous.filter(job => !created.some(item => item.id === job.id))]);
        refreshJobs();
        window.requestAnimationFrame(() => document.getElementById(`plan-${domId(post.id)}-publish`)?.focus());
    };

    const closePublish = () => {
        const post = publishPost;
        setPublishPost(null);
        if (post) window.requestAnimationFrame(() => document.getElementById(`plan-${domId(post.id)}-publish`)?.focus());
    };

    const setPostDraft = (postId, draft) => setPlan(previous => previous && ({
        ...previous,
        posts: previous.posts.map(post => (post.id === postId ? { ...post, draft } : post)),
    }));

    // Shows the edit at once and saves it after `delay`
    const changeDraft = (post, change, delay = DRAFT_SAVE_DELAY_MS) => {
        if (!post.draft) return;
        setPlan(previous => previous && ({
            ...previous,
            posts: previous.posts.map(item => (item.id === post.id && item.draft ? { ...item, draft: { ...item.draft, ...change } } : item)),
        }));
        const key = draftKey(post.id);
        const entry = draftSaves.current[key] || { weekStart, postId: post.id, changes: {} };
        draftSaves.current[key] = entry;
        entry.changes = { ...entry.changes, ...change };
        clearTimeout(entry.timer);
        entry.timer = setTimeout(() => flushDraft(key), delay);
        setDraftStatus(previous => ({ ...previous, [key]: '' }));
    };

    const handleDraft = async (post, { rewrite = false } = {}) => {
        const key = draftKey(post.id);
        if (rewrite && post.draft && (isEditedDraft(post.draft) || draftStatus[key] === 'saved' || draftSaves.current[key])
            && !window.confirm('Rewrite this draft? New hooks, caption and hashtags replace your edits.')) {
            return;
        }
        // A new draft replaces edits that haven't gone out yet
        const pending = draftSaves.current[key];
        if (pending && !pending.inFlight) {
            clearTimeout(pending.timer);
            delete draftSaves.current[key];
        }
        const id = loadId.current;
        setDrafting(previous => ({ ...previous, [key]: true }));
        setPostError(null);
        try {
            const updated = normalizePlan(await api.draftPlanPost(weekStart, post.id));
            if (id !== loadId.current) return;
            const draft = updated?.posts.find(item => item.id === post.id)?.draft;
            if (!draft) throw new Error('No draft');
            // Only this post changes, so edits on other posts stay as they are
            setPostDraft(post.id, draft);
            setDraftStatus(previous => ({ ...previous, [key]: '' }));
            setHookInputs(previous => without(previous, key));
        } catch (e) {
            if (isLimitError(e) || id !== loadId.current) return;
            setPostError({
                postId: post.id,
                // The server's words for a request it turned down; our own for its failures
                message: (e.response?.status < 500 && toText(e.response?.data?.error))
                    || (rewrite ? "Couldn't rewrite the draft. Your current draft is kept, so please try again." : "Couldn't draft this post. Please try again in a moment."),
            });
        } finally {
            setDrafting(previous => without(previous, key));
        }
    };

    const addHashtag = (post) => {
        const key = draftKey(post.id);
        // "#a #b" and "a, b" are two hashtags; "meal planning" is #mealplanning
        const tags = (hashtagInputs[key] || '').split(/,|\s+(?=#)/).map(toHashtag).filter(Boolean);
        setHashtagInputs(previous => without(previous, key));
        if (!tags.length) return;
        const next = uniqueHashtags([...post.draft.hashtags, ...tags]).slice(0, MAX_HASHTAGS);
        if (next.length !== post.draft.hashtags.length) changeDraft(post, { hashtags: next });
    };

    const removeHashtag = (post, tag) => {
        changeDraft(post, { hashtags: post.draft.hashtags.filter(item => item !== tag) });
        // The chip's button is gone, so focus moves to the add field
        document.getElementById(`plan-${domId(post.id)}-tag-input`)?.focus();
    };

    const applyOwnHook = (post) => {
        const key = draftKey(post.id);
        const hook = (hookInputs[key] || '').trim();
        setHookInputs(previous => without(previous, key));
        if (hook && hook !== post.draft.hook) changeDraft(post, { hook });
    };

    const automation = { ...readAutomation(currentProduct), ...automationOverrides };

    // Saves at once; the switch shows the choice while it saves and flips back if it can't
    const saveAutomation = async (key, on) => {
        if (!currentProduct) return;
        const seq = (automationSeq.current[key] || 0) + 1;
        automationSeq.current[key] = seq;
        const saving = automationSaving.current;
        if (saving.count === 0) saving.failed = null;
        saving.count += 1;
        setAutomationOverrides(previous => ({ ...previous, [key]: on }));
        setAutomationStatus({ state: 'saving' });
        try {
            await updateProduct(currentProduct.id, { automation: { [key]: on } });
        } catch (e) {
            if (automationSeq.current[key] === seq) saving.failed = { key, on };
        } finally {
            if (automationSeq.current[key] === seq) setAutomationOverrides(previous => without(previous, key));
            saving.count -= 1;
            if (saving.count === 0) setAutomationStatus(saving.failed ? { state: 'error', retry: saving.failed } : { state: 'saved' });
        }
    };

    const toggleAutomations = () => {
        setShowAutomations(!showAutomations);
        storage.set('planAutomationsOpen', String(!showAutomations));
    };

    const applyPreset = (preset) => {
        setPlanPreferences(previous => ({
            ...previous,
            postsPerWeek: preset.postsPerWeek,
            platforms: mainPlatforms(currentProduct, previous.platforms),
            contentTypes: preset.contentTypes,
            contentGoals: preset.goal(currentProduct),
        }));
    };

    const setPreference = (field, value) => setPlanPreferences(previous => ({ ...previous, [field]: value }));

    const togglePreference = (field, option, checked) => {
        setPlanPreferences(previous => ({
            ...previous,
            [field]: checked ? [...previous[field], option] : previous[field].filter(item => item !== option),
        }));
    };

    const changeView = (next) => {
        setView(next);
        storage.set('planView', next);
    };

    const openSettings = () => {
        setShowSettings(true);
        focusField('targetAudience');
    };

    const isLoadingPlan = busy === 'loading';
    const showSkeleton = isLoadingPlan || busy === 'generating';
    const isCurrentWeek = weekStart === getWeekStart();
    const isPastWeek = weekStart < getWeekStart();
    const hasPosted = posts.some(post => post.status === 'posted');
    const latest = plan?.reviews[plan.reviews.length - 1];
    const lastReview = latest && (latest.summary || latest.learnings.length || latest.changes) ? latest : null;
    const postedCount = posts.filter(post => post.status === 'posted').length;
    const today = formatLocalDate(new Date());
    const days = posts.some(post => post.day === UNSCHEDULED) ? [...WEEK_DAYS, UNSCHEDULED] : WEEK_DAYS;
    const icpName = (id) => {
        const icp = id && icps.find(item => item.id === id);
        return icp && toText(icp.name) ? shortName(icp.name) : '';
    };

    const renderRequiredLabel = (field, label, htmlFor) => (
        <div className="ls-plan-label-row">
            {htmlFor
                ? <label className="ls-field-label" htmlFor={htmlFor}>{label}<span className="ls-plan-req" aria-hidden="true"> *</span></label>
                : <span className="ls-field-label" id={`plan-${field}-label`}>{label}<span className="ls-plan-req" aria-hidden="true"> *</span></span>}
            {fieldError(field) && <span className="ls-plan-field-error" id={`plan-${field}-error`}>Required</span>}
        </div>
    );

    const renderChoices = (field, label, options) => (
        <div className="ls-field">
            {renderRequiredLabel(field, label)}
            <div
                className="ls-choices"
                id={`plan-${field}`}
                role="group"
                aria-labelledby={`plan-${field}-label`}
                aria-describedby={fieldError(field) ? `plan-${field}-error` : undefined}
            >
                {options.map(option => (
                    <label key={option} className="ls-choice">
                        <input
                            type="checkbox"
                            checked={planPreferences[field].includes(option)}
                            onChange={(e) => togglePreference(field, option, e.target.checked)}
                        />
                        {option}
                    </label>
                ))}
            </div>
        </div>
    );

    const renderAutomationStatus = () => {
        const { state, retry } = automationStatus;
        if (state === 'saving') return <span className="ls-plan-save is-saving" role="status"><span className="ls-spinner" />Saving…</span>;
        if (state === 'saved') return <span className="ls-plan-save is-saved" role="status"><Icon name="check" size={16} />Saved</span>;
        if (state === 'error') {
            return (
                <span className="ls-plan-save is-error" role="alert">
                    <Icon name="error" size={16} />Couldn't save.
                    <button type="button" className="ls-link" onClick={() => saveAutomation(retry.key, retry.on)}>Try again</button>
                </span>
            );
        }
        return <span className="ls-plan-save" />;
    };

    const renderAutomations = () => {
        const onCount = AUTOMATIONS.filter(item => automation[item.id]).length;
        return (
            <section className={`ls-card ls-plan-auto${showAutomations ? ' is-open' : ''}`} aria-labelledby="plan-auto-title">
                <div className="ls-plan-auto-head">
                    <h2 className="ls-card-title ls-plan-heading" id="plan-auto-title">
                        <button
                            type="button"
                            className="ls-plan-auto-toggle"
                            onClick={toggleAutomations}
                            aria-expanded={showAutomations}
                            aria-controls="plan-auto-body"
                        >
                            <Icon name="bolt" size={18} className="ls-plan-auto-icon" />
                            Automations
                            <span className={`ls-plan-auto-count${onCount ? ' is-on' : ''}`}>
                                {onCount === 0 ? 'Off' : onCount === AUTOMATIONS.length ? 'All on' : `${onCount} on`}
                            </span>
                            <span className="ls-plan-auto-summary">Plan, draft and review your weeks for you</span>
                            <Icon name="expand_more" size={20} className="ls-plan-auto-chevron" />
                        </button>
                    </h2>
                </div>
                {showAutomations && (
                    <div className="ls-plan-auto-body" id="plan-auto-body">
                        <div className="ls-plan-auto-list">
                            {AUTOMATIONS.map(item => (
                                <label key={item.id} className="ls-plan-auto-row">
                                    <span className="ls-plan-auto-text">
                                        <span className="ls-plan-auto-title" id={`plan-${item.id}-label`}>{item.label}</span>
                                        <span className="ls-plan-auto-desc" id={`plan-${item.id}-desc`}>{item.description}</span>
                                    </span>
                                    <span className="ls-plan-switch">
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            checked={automation[item.id]}
                                            onChange={(e) => saveAutomation(item.id, e.target.checked)}
                                            aria-labelledby={`plan-${item.id}-label`}
                                            aria-describedby={`plan-${item.id}-desc`}
                                        />
                                        <span className="ls-plan-switch-track" aria-hidden="true">
                                            <span className="ls-plan-switch-thumb" />
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="ls-plan-auto-foot">
                            <p className="ls-plan-auto-note">
                                Each automatic plan, draft or review uses a generation from your plan. They run at your email send time,
                                set in{' '}
                                <button type="button" className="ls-link" onClick={() => navigate('/dashboard/notifications')}>
                                    Account → Email updates
                                </button>.
                            </p>
                            {renderAutomationStatus()}
                        </div>
                    </div>
                )}
            </section>
        );
    };

    const renderPresets = () => (
        <div className="ls-field">
            <span className="ls-field-label" id="plan-preset-label">Start from a preset</span>
            <div className="ls-choices" role="radiogroup" aria-labelledby="plan-preset-label" aria-describedby="plan-preset-hint">
                {PRESETS.map(preset => (
                    <label key={preset.id} className="ls-choice">
                        <input
                            type="radio"
                            name="plan-preset"
                            checked={presetMatches(preset, planPreferences, currentProduct)}
                            onChange={() => applyPreset(preset)}
                        />
                        {preset.label}
                        <span className="ls-plan-preset-meta">{preset.postsPerWeek}/wk</span>
                    </label>
                ))}
            </div>
            <span className="ls-hint" id="plan-preset-hint">
                Fills posts per week, platforms, content types and goals below. You can still change any of them.
            </span>
        </div>
    );

    const renderSettings = () => (
        <section className="ls-card ls-plan-settings" aria-labelledby="plan-settings-title">
            <div className="ls-card-header">
                <div>
                    <h2 className="ls-card-title ls-plan-heading" id="plan-settings-title">Plan settings</h2>
                    <div className="ls-card-sub">
                        The AI uses these, your strategy and past results (see Memory) to plan posts and set targets.
                    </div>
                </div>
                <span className="ls-plan-req-note"><span className="ls-plan-req">*</span> Required</span>
            </div>

            <div className="ls-form">
                {renderPresets()}
                <div className="ls-form-grid">
                    <div className="ls-field is-wide">
                        {renderRequiredLabel('targetAudience', 'Target audience', 'plan-targetAudience')}
                        <input
                            id="plan-targetAudience"
                            className="ls-input"
                            type="text"
                            value={planPreferences.targetAudience}
                            onChange={(e) => setPreference('targetAudience', e.target.value)}
                            placeholder="Who the posts are for, e.g. busy parents who cook at home"
                            aria-required="true"
                            aria-invalid={fieldError('targetAudience') || undefined}
                            aria-describedby={fieldError('targetAudience') ? 'plan-targetAudience-error' : undefined}
                        />
                    </div>
                    <div className="ls-field is-wide">
                        {renderRequiredLabel('contentGoals', 'Content goals', 'plan-contentGoals')}
                        <input
                            id="plan-contentGoals"
                            className="ls-input"
                            type="text"
                            value={planPreferences.contentGoals}
                            onChange={(e) => setPreference('contentGoals', e.target.value)}
                            placeholder="e.g. Grow app installs from short video"
                            aria-required="true"
                            aria-invalid={fieldError('contentGoals') || undefined}
                            aria-describedby={fieldError('contentGoals') ? 'plan-contentGoals-error' : undefined}
                        />
                    </div>
                    <div className="ls-field">
                        <label className="ls-label" htmlFor="plan-toneOfVoice">Tone of voice</label>
                        <input
                            id="plan-toneOfVoice"
                            className="ls-input"
                            type="text"
                            value={planPreferences.toneOfVoice}
                            onChange={(e) => setPreference('toneOfVoice', e.target.value)}
                            placeholder="e.g. Friendly, a little playful"
                        />
                    </div>
                    <div className="ls-field">
                        <label className="ls-label" htmlFor="plan-industryFocus">Industry focus</label>
                        <input
                            id="plan-industryFocus"
                            className="ls-input"
                            type="text"
                            value={planPreferences.industryFocus}
                            onChange={(e) => setPreference('industryFocus', e.target.value)}
                            placeholder="e.g. Food & drink apps"
                        />
                    </div>
                    <div className="ls-field">
                        <label className="ls-label" htmlFor="plan-postsPerWeek">Posts per week</label>
                        <select
                            id="plan-postsPerWeek"
                            className="ls-select"
                            value={planPreferences.postsPerWeek}
                            onChange={(e) => setPreference('postsPerWeek', e.target.value)}
                        >
                            {postsPerWeekOptions.map(num => (
                                <option key={num} value={String(num)}>{num} {num === 1 ? 'post' : 'posts'}</option>
                            ))}
                        </select>
                    </div>
                    <div className="ls-field">
                        <div className="ls-plan-label-row">
                            <label className="ls-field-label" htmlFor="plan-linkDestination">Link destination</label>
                            {linkTouched && linkInvalid && (
                                <span className="ls-plan-field-error" id="plan-linkDestination-error">Start with https://</span>
                            )}
                        </div>
                        <input
                            id="plan-linkDestination"
                            className="ls-input"
                            type="url"
                            inputMode="url"
                            value={planPreferences.linkDestination}
                            onChange={(e) => setPreference('linkDestination', e.target.value)}
                            onBlur={() => setLinkTouched(true)}
                            placeholder="https://yourstore.com"
                            aria-invalid={(linkTouched && linkInvalid) || undefined}
                            aria-describedby={linkTouched && linkInvalid ? 'plan-linkDestination-error plan-linkDestination-hint' : 'plan-linkDestination-hint'}
                        />
                        <span className="ls-hint" id="plan-linkDestination-hint">Posts you mark as posted get a tracked short link to this page.</span>
                    </div>
                </div>

                {renderChoices('platforms', 'Platforms', platformOptions)}
                {renderChoices('contentTypes', 'Content types', contentTypeOptions)}
                <BestTimesHint bestTimes={bestTimes} />
            </div>
        </section>
    );

    const renderMetrics = (post) => {
        const actual = getActual(post);
        return (
            <div className="ls-plan-metrics">
                {METRICS.map(metric => {
                    const target = post.targets[metric.id];
                    const value = actual?.[metric.id];
                    const tone = value == null ? '' : value >= target ? ' is-hit' : ' is-miss';
                    const saved = entryByPostId[post.id]?.[metric.id];
                    const inputValue = saved === '' || saved == null ? '' : Number(saved) || 0;
                    const inputId = `plan-${post.id}-${metric.id}`;
                    return (
                        <div key={metric.id} className="ls-plan-metric">
                            {post.status === 'posted' && metric.id !== 'clicks' ? (
                                <>
                                    <label className="ls-metric-label" htmlFor={inputId}>{metric.label}</label>
                                    <input
                                        id={inputId}
                                        className={`ls-input is-compact ls-plan-result${tone}`}
                                        type="number"
                                        min="0"
                                        inputMode="numeric"
                                        value={inputValue}
                                        onChange={(e) => handleResultChange(post, metric.id, e.target.value)}
                                        placeholder="—"
                                        aria-describedby={`${inputId}-target`}
                                    />
                                </>
                            ) : (
                                <>
                                    <span className="ls-metric-label">{metric.label}</span>
                                    <span className={`ls-plan-metric-value${tone}`}>
                                        {value != null ? value.toLocaleString() : '—'}
                                    </span>
                                </>
                            )}
                            <span className="ls-plan-metric-target" id={`${inputId}-target`}>of {target.toLocaleString()}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderPostDay = (day) => {
        if (day === UNSCHEDULED) return <span className="ls-plan-post-day">No day</span>;
        const date = dayDate(weekStart, day);
        const isToday = date === today;
        return (
            <span className={`ls-plan-post-day${isToday ? ' is-today' : ''}`}>
                {parseLocalDate(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                {isToday && <span className="ls-plan-today">Today</span>}
            </span>
        );
    };

    const renderDraftStatus = (post) => {
        const key = draftKey(post.id);
        const state = draftStatus[key];
        if (state === 'saving') return <span className="ls-plan-save is-saving" role="status"><span className="ls-spinner" />Saving…</span>;
        if (state === 'saved') return <span className="ls-plan-save is-saved" role="status"><Icon name="check" size={16} />Saved</span>;
        if (state === 'error') {
            return (
                <span className="ls-plan-save is-error" role="alert">
                    <Icon name="error" size={16} />Not saved.
                    <button type="button" className="ls-link" onClick={() => flushDraft(key)}>Try again</button>
                </span>
            );
        }
        return <span className="ls-plan-save" />;
    };

    const renderDraft = (post) => {
        const { draft } = post;
        const key = draftKey(post.id);
        const id = `plan-${domId(post.id)}`;
        const isRewriting = Boolean(drafting[key]);
        const ownHook = draft.hook && !draft.hooks.includes(draft.hook) ? draft.hook : '';
        const length = draftText(draft).length;
        const limit = captionLimit(post.platform);
        const isOver = length > limit;
        const isTagsFull = draft.hashtags.length >= MAX_HASHTAGS;
        return (
            <div className={`ls-plan-draft ls-enter${isRewriting ? ' is-busy' : ''}`} role="group" aria-busy={isRewriting} aria-label="Draft">
                <div className="ls-plan-draft-head">
                    <span className="ls-plan-draft-title"><Icon name="edit_note" size={18} />Draft</span>
                    {draft.auto && <span className="ls-chip ls-plan-auto-chip" title="Drafted automatically">Auto</span>}
                    <button
                        type="button"
                        className="ls-btn ls-btn-ghost ls-plan-rewrite"
                        onClick={() => handleDraft(post, { rewrite: true })}
                        disabled={isRewriting}
                    >
                        {isRewriting ? <span className="ls-spinner" /> : <Icon name="refresh" size={16} />}Rewrite
                    </button>
                </div>

                <div className="ls-plan-draft-field">
                    <span className="ls-label" id={`${id}-hooks-label`}>Pick a hook</span>
                    <div className="ls-choices ls-plan-hooks" role="radiogroup" aria-labelledby={`${id}-hooks-label`}>
                        {[...draft.hooks, ...(ownHook ? [ownHook] : [])].map(hook => (
                            <label key={hook} className="ls-choice ls-plan-hook">
                                <input
                                    type="radio"
                                    name={`${id}-hook`}
                                    checked={draft.hook === hook}
                                    onChange={() => changeDraft(post, { hook })}
                                />
                                <span className="ls-plan-hook-text">{hook}</span>
                                {hook === ownHook && <span className="ls-plan-hook-own">Your own</span>}
                            </label>
                        ))}
                    </div>
                    <input
                        className="ls-input is-compact ls-plan-own-hook"
                        type="text"
                        value={hookInputs[key] || ''}
                        onChange={(e) => setHookInputs(previous => ({ ...previous, [key]: e.target.value }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                applyOwnHook(post);
                            }
                        }}
                        onBlur={() => applyOwnHook(post)}
                        placeholder="Or write your own hook"
                        maxLength={500}
                        aria-label="Write your own hook"
                    />
                </div>

                <div className="ls-plan-draft-field">
                    <div className="ls-plan-label-row">
                        <label className="ls-label" htmlFor={`${id}-caption`}>Caption</label>
                        <span
                            className={`ls-plan-count${isOver ? ' is-over' : ''}`}
                            id={`${id}-count`}
                            title={`Hook, caption and hashtags together, of ${limit.toLocaleString()} on ${platformName(post.platform)}`}
                        >
                            {`${length.toLocaleString()} / ${limit.toLocaleString()}${isOver ? ' · too long' : ''}`}
                        </span>
                    </div>
                    {/* The caption follows the hook, so the post reads in order */}
                    <div className="ls-plan-compose">
                        <div className={`ls-plan-caption-hook${draft.hook ? '' : ' is-empty'}`}>{draft.hook || 'Pick a hook above'}</div>
                        <textarea
                            id={`${id}-caption`}
                            className="ls-textarea is-compact ls-plan-caption"
                            rows={4}
                            maxLength={5000}
                            value={draft.caption}
                            onChange={(e) => changeDraft(post, { caption: e.target.value }, CAPTION_SAVE_DELAY_MS)}
                            onBlur={() => flushDraft(key)}
                            aria-describedby={`${id}-count`}
                        />
                    </div>
                </div>

                <div className="ls-plan-draft-field">
                    <span className="ls-label" id={`${id}-tags-label`}>Hashtags</span>
                    <div className="ls-chips ls-plan-tags" role="group" aria-labelledby={`${id}-tags-label`}>
                        {draft.hashtags.map(tag => (
                            <span key={tag} className="ls-chip is-medium">
                                {tag}
                                <button
                                    type="button"
                                    className="ls-chip-remove"
                                    aria-label={`Remove ${tag}`}
                                    onClick={() => removeHashtag(post, tag)}
                                >
                                    <Icon name="close" size={14} />
                                </button>
                            </span>
                        ))}
                        <input
                            id={`${id}-tag-input`}
                            className="ls-input is-compact ls-plan-tag-input"
                            type="text"
                            value={hashtagInputs[key] || ''}
                            onChange={(e) => setHashtagInputs(previous => ({ ...previous, [key]: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ',') {
                                    e.preventDefault();
                                    addHashtag(post);
                                }
                            }}
                            onBlur={() => addHashtag(post)}
                            placeholder={isTagsFull ? `${MAX_HASHTAGS} max` : 'Add hashtag'}
                            disabled={isTagsFull}
                            aria-label={isTagsFull ? `Add a hashtag (${MAX_HASHTAGS} at most)` : 'Add a hashtag'}
                        />
                    </div>
                </div>

                <div className="ls-plan-draft-foot">
                    <CopyButton text={draftCopyText(draft, post.shortUrl)} label="Copy post" />
                    {renderDraftStatus(post)}
                </div>
            </div>
        );
    };

    // In the platform view the section names the platform, so the card shows the day instead
    const renderPost = (post, { byPlatform = false } = {}) => {
        const kind = kindForContentType(post.contentType);
        const type = CREATE_KINDS[kind].type;
        const colors = TYPE_STYLES[type];
        const isPosted = post.status === 'posted';
        const isDraft = post.status === 'draft';
        const channel = shortChannel(post.platform);
        const icp = icpName(post.icpId);
        const isDrafting = Boolean(drafting[draftKey(post.id)]);
        const won = isPosted ? winners[post.id] : null;
        const meta = [post.time, post.contentType && post.contentType.toLowerCase() !== type.toLowerCase() ? post.contentType : null, icp && `For ${icp}`]
            .filter(Boolean).join(' · ');
        return (
            <article key={post.id} className={`ls-plan-post${isPosted ? ' is-posted' : ''}`}>
                <div className="ls-plan-post-head">
                    {byPlatform && renderPostDay(post.day)}
                    <span className="ls-plan-type" style={{ background: colors.bg, color: colors.fg }}>
                        {[type, !byPlatform && channel && channel.toLowerCase() !== type.toLowerCase() ? channel : null].filter(Boolean).join(' · ')}
                    </span>
                    {meta && <span className="ls-plan-post-meta">{meta}</span>}
                    {won && <span className="ls-wins-chip"><Icon name="emoji_events" size={14} />Beat target</span>}
                    <span className={`ls-plan-post-status${isPosted ? ' is-posted' : isDraft ? ' is-draft' : ''}`}>
                        {isPosted && <Icon name="check_circle" size={14} />}
                        {isPosted ? 'Posted' : isDraft ? 'Draft' : 'Planned'}
                    </span>
                </div>

                <div className="ls-plan-post-body">
                    <div className="ls-plan-post-text">
                        <div className="ls-plan-post-theme">{post.theme || post.hook || 'Untitled post'}</div>
                        {post.hook && post.theme && <div className="ls-plan-post-hook">“{post.hook}”</div>}
                        {post.idea && <div className="ls-plan-post-idea">{post.idea}</div>}
                        {post.why && <div className="ls-plan-post-why"><b>Why:</b> {post.why}</div>}
                    </div>
                    {renderMetrics(post)}
                </div>

                {post.draft && renderDraft(post)}

                <div className="ls-plan-post-actions">
                    {!isPosted && !post.draft && (
                        <button
                            type="button"
                            className="ls-btn ls-btn-secondary"
                            onClick={() => handleDraft(post)}
                            disabled={isDrafting || ['loading', 'generating', 'reviewing'].includes(busy)}
                        >
                            {isDrafting ? <span className="ls-spinner" /> : <Icon name="auto_awesome" size={18} />}Draft it
                        </button>
                    )}
                    {!isPosted && (
                        <button
                            type="button"
                            className="ls-btn ls-btn-secondary"
                            onClick={() => navigate(
                                `/dashboard/${kind}${post.icpId ? `?icp=${post.icpId}` : ''}`,
                                {
                                    state: {
                                        seed: {
                                            topic: post.idea || post.theme,
                                            hook: post.draft?.hook || post.hook,
                                            platform: post.platform,
                                            // The Create flow saves back to this post and can publish it
                                            planWeek: weekStart,
                                            postId: post.id,
                                            day: post.day,
                                            time: post.time,
                                            theme: post.theme,
                                            shortUrl: post.shortUrl,
                                            caption: post.draft?.caption || '',
                                            hashtags: post.draft?.hashtags || [],
                                        },
                                    },
                                }
                            )}
                        >
                            <Icon name="edit_note" size={18} />Create
                        </button>
                    )}
                    {!isPosted && (
                        <button
                            type="button"
                            id={`plan-${domId(post.id)}-publish`}
                            className="ls-btn ls-btn-secondary ls-publish-btn"
                            onClick={() => setPublishPost(post)}
                            disabled={['loading', 'generating'].includes(busy)}
                        >
                            <Icon name="send" size={18} />Publish
                        </button>
                    )}
                    {!isPosted ? (
                        <button
                            type="button"
                            className="ls-btn ls-btn-secondary"
                            onClick={() => handlePublish(post)}
                            disabled={!!busy}
                        >
                            {busy === post.id ? <span className="ls-spinner" /> : <Icon name="task_alt" size={18} />}
                            {planPreferences.linkDestination ? 'Mark posted & get link' : 'Mark posted'}
                        </button>
                    ) : post.shortUrl ? (
                        <button
                            type="button"
                            className={`ls-btn ls-btn-pill ls-plan-link${copiedPostId === post.id ? ' is-on' : ''}`}
                            onClick={() => handleCopy(post)}
                            title="Copy tracked link"
                            aria-label={copiedPostId === post.id ? 'Link copied' : `Copy tracked link ${post.shortUrl}`}
                        >
                            <Icon name={copiedPostId === post.id ? 'check' : 'link'} size={15} />
                            <span className="ls-plan-link-text">{post.shortUrl.replace(/^https?:\/\//, '')}</span>
                        </button>
                    ) : (
                        <span className="ls-plan-muted">
                            No tracked link.{' '}
                            <button type="button" className="ls-link" onClick={() => {
                                setShowSettings(true);
                                focusField('linkDestination');
                            }}>
                                Add a link destination
                            </button>
                        </span>
                    )}
                    <RepurposeMenu
                        source={{
                            platform: post.platform,
                            contentType: post.contentType,
                            title: post.theme,
                            hook: post.draft?.hook || post.hook,
                            caption: post.draft?.caption,
                            idea: post.idea,
                        }}
                    />
                    {copiedPostId === post.id && <span className="ls-plan-copied" role="status">Copied</span>}
                </div>
                <PublishStatus
                    jobs={jobs.filter(job => job.planPostId === post.id)}
                    published={post.published}
                    busy={jobBusy}
                    onCancel={(job) => handleJobAction(post, job, 'cancel')}
                    onRetry={(job) => handleJobAction(post, job, 'retry')}
                />
                {won && (
                    <div className="ls-wins-row">
                        <span className="ls-wins-text">
                            <Icon name="trending_up" size={18} />
                            Beat its {listMetrics(won)} target. Make more of what works.
                        </span>
                        <div className="ls-wins-actions">
                            <button type="button" className="ls-btn ls-btn-secondary" onClick={() => handleFollowUp(post)} disabled={!!followUpBusy}>
                                {followUpBusy === post.id ? <span className="ls-spinner" /> : <Icon name="content_copy" size={16} />}Do another like this
                            </button>
                            <button type="button" className="ls-btn ls-btn-secondary" onClick={() => makeAd(post)}>
                                <Icon name="campaign" size={16} />Make it an ad
                            </button>
                        </div>
                    </div>
                )}
                {postNotice?.postId === post.id && (
                    <Alert
                        severity={postNotice.severity || 'success'}
                        onClose={() => setPostNotice(null)}
                        action={postNotice.weekStart ? (
                            <button type="button" className="ls-btn ls-btn-secondary ls-plan-retry" onClick={() => setWeekStart(postNotice.weekStart)}>View week</button>
                        ) : null}
                    >
                        {postNotice.message}
                    </Alert>
                )}
                {postError?.postId === post.id && (
                    <Alert severity="error" onClose={() => setPostError(null)}>{postError.message}</Alert>
                )}
            </article>
        );
    };

    const renderDayLabel = (day, index) => {
        if (day === UNSCHEDULED) return <div className="ls-plan-day-label"><b>No day</b><span>Unscheduled</span></div>;
        const date = addDays(weekStart, index);
        const isToday = date === today;
        return (
            <div className={`ls-plan-day-label${isToday ? ' is-today' : ''}`}>
                <b>{day}</b>
                <span>
                    {parseLocalDate(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {isToday && <span className="ls-plan-today">Today</span>}
                </span>
            </div>
        );
    };

    const renderTotals = () => (
        <section className="ls-card ls-plan-totals" aria-label="This week's results against targets">
            {METRICS.map(metric => {
                const { target, actual } = totals[metric.id];
                const progress = target ? Math.min(100, (actual / target) * 100) : 0;
                return (
                    <div key={metric.id} className="ls-plan-total">
                        <div className="ls-metric-label">{metric.label}<span className="ls-plan-total-suffix"> this week</span></div>
                        <div className="ls-metric-value">
                            {showSkeleton ? <span className="ls-skeleton-text" /> : actual.toLocaleString()}
                        </div>
                        <div className="ls-plan-metric-target">
                            {showSkeleton ? <span className="ls-skeleton-text" style={{ width: '7em' }} /> : `of ${target.toLocaleString()} target`}
                        </div>
                        <div className="ls-progress" role="presentation"><div style={{ width: `${progress}%` }} /></div>
                    </div>
                );
            })}
        </section>
    );

    const renderPlatformTotals = (groupTotals) => (
        <div className="ls-plan-platform-totals">
            {METRICS.map(metric => {
                const { target, actual } = groupTotals[metric.id];
                return (
                    <span key={metric.id} className="ls-plan-platform-total">
                        <span className="ls-plan-platform-total-label">{metric.label}</span>
                        <b>{actual.toLocaleString()}</b>
                        <span className="ls-plan-metric-target">of {target.toLocaleString()}</span>
                    </span>
                );
            })}
        </div>
    );

    const renderPlatforms = () => {
        if (showSkeleton) {
            return (
                <div className="ls-plan-platforms">
                    {[0, 1, 2].map(index => (
                        <div key={index} className="ls-plan-platform">
                            <div className="ls-plan-platform-head">
                                <div className="ls-plan-platform-name">
                                    <span className="ls-skeleton-text" style={{ width: '6em' }} />
                                </div>
                                <span className="ls-skeleton-text" style={{ width: '18em' }} />
                            </div>
                            <div className="ls-plan-day-posts">
                                <div className="ls-skeleton ls-plan-post-skeleton" />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        if (!platformGroups.length) {
            return <div className="ls-plan-platforms"><span className="ls-plan-rest">No posts planned this week.</span></div>;
        }
        return (
            <div className="ls-plan-platforms ls-stagger">
                {platformGroups.map((group, index) => {
                    const headingId = `plan-platform-${index}`;
                    return (
                        <section key={group.key} className="ls-plan-platform" style={{ '--i': index }} aria-labelledby={headingId}>
                            <div className="ls-plan-platform-head">
                                <div className="ls-plan-platform-name">
                                    <h3 id={headingId}>{group.name}</h3>
                                    <span>{group.posts.length} {group.posts.length === 1 ? 'post' : 'posts'}</span>
                                </div>
                                {renderPlatformTotals(group.totals)}
                            </div>
                            <div className="ls-plan-day-posts">
                                {group.posts.map(post => renderPost(post, { byPlatform: true }))}
                            </div>
                        </section>
                    );
                })}
            </div>
        );
    };

    const renderWeek = () => {
        const scheduled = posts.filter(post => post.status !== 'posted').length;
        return (
            <section
                className={`ls-card ls-plan-week${busy === 'reviewing' ? ' is-busy' : ''}`}
                aria-labelledby="plan-week-title"
                aria-busy={showSkeleton || busy === 'reviewing'}
            >
                <div className="ls-card-header ls-plan-week-header">
                    <div className="ls-plan-week-heading">
                        <h2 className="ls-card-title ls-plan-heading" id="plan-week-title">Posts</h2>
                        <span className="ls-card-sub">
                            {showSkeleton
                                ? (busy === 'generating' ? 'Planning your week…' : <span className="ls-skeleton-text" style={{ width: '8em' }} />)
                                : [`${scheduled} planned`, postedCount ? `${postedCount} posted` : null].filter(Boolean).join(' · ')}
                        </span>
                        {plan?.auto && !showSkeleton && (
                            <span className="ls-plan-auto-plan">
                                <span className="ls-chip ls-plan-auto-chip">Auto</span>
                                Planned automatically · review and tweak
                            </span>
                        )}
                    </div>
                    <div className="ls-segmented ls-plan-view" role="group" aria-label="Show posts">
                        {VIEWS.map(option => (
                            <button
                                key={option.id}
                                type="button"
                                aria-pressed={view === option.id}
                                onClick={() => changeView(option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                {view === 'platform' ? renderPlatforms() : showSkeleton ? (
                    <div className="ls-plan-days">
                        {WEEK_DAYS.map((day, index) => (
                            <div key={day} className="ls-plan-day">
                                {renderDayLabel(day, index)}
                                <div className="ls-plan-day-posts">
                                    {index % 2 === 0
                                        ? <div className="ls-skeleton ls-plan-post-skeleton" />
                                        : <span className="ls-plan-rest">No post</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="ls-plan-days ls-stagger">
                        {days.map((day, index) => {
                            const dayPosts = posts.filter(post => post.day === day);
                            return (
                                <div key={day} className="ls-plan-day" style={{ '--i': index }}>
                                    {renderDayLabel(day, index)}
                                    <div className="ls-plan-day-posts">
                                        {dayPosts.length === 0
                                            ? <span className="ls-plan-rest">No post</span>
                                            : dayPosts.map(post => renderPost(post))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        );
    };

    const renderEmpty = () => (isPastWeek ? (
        <section className="ls-card ls-plan-empty">
            <Icon name="event_busy" size={28} className="ls-plan-empty-icon" />
            <div className="ls-card-title">No plan this week</div>
            <p className="ls-empty">
                There was no content plan for {formatWeekRange(weekStart)}. Plans are made for this week and the weeks ahead.
            </p>
            <button type="button" className="ls-btn ls-btn-secondary" onClick={() => setWeekStart(getWeekStart())}>
                <Icon name="today" size={18} />Back to this week
            </button>
        </section>
    ) : (
        <section className="ls-card ls-plan-empty">
            <Icon name="calendar_month" size={28} className="ls-plan-empty-icon" />
            <div className="ls-card-title">No plan for this week yet</div>
            <p className="ls-empty">
                {showSettings
                    ? 'Check the plan settings above, then generate a week of posts with views, likes and click targets for each.'
                    : 'Generate a week of posts with views, likes and click targets for each. Start from the plan settings.'}
            </p>
            {showSettings ? (
                <button type="button" className="ls-btn ls-btn-secondary" onClick={handleGeneratePlan} disabled={!!busy}>
                    <Icon name="auto_awesome" size={18} />Generate this week's plan
                </button>
            ) : (
                <button type="button" className="ls-btn ls-btn-secondary" onClick={openSettings}>
                    <Icon name="tune" size={18} />Open plan settings
                </button>
            )}
        </section>
    ));

    return (
        <>
            <div className="ls-page-header">
                <div className="ls-plan-title">
                    <div className="ls-eyebrow">{currentProduct?.name ? `Plan · ${currentProduct.name}` : 'Plan'}</div>
                    <h1 className="ls-page-title">Content plan</h1>
                    <div className="ls-plan-weeknav">
                        <button
                            type="button"
                            className="ls-btn ls-btn-icon is-outlined"
                            onClick={() => setWeekStart(addDays(weekStart, -7))}
                            aria-label="Previous week"
                        >
                            <Icon name="chevron_left" />
                        </button>
                        <button
                            type="button"
                            className="ls-btn ls-btn-icon is-outlined"
                            onClick={() => setWeekStart(addDays(weekStart, 7))}
                            aria-label="Next week"
                        >
                            <Icon name="chevron_right" />
                        </button>
                        <span className="ls-plan-week-label" aria-live="polite"><span key={formatWeekRange(weekStart)} className="ls-count-in">{formatWeekRange(weekStart)}</span></span>
                        {isCurrentWeek
                            ? <span className="ls-plan-today">This week</span>
                            : <button type="button" className="ls-link" onClick={() => setWeekStart(getWeekStart())}>Back to this week</button>}
                    </div>
                </div>
                <div className="ls-actions">
                    <button
                        type="button"
                        className="ls-btn ls-btn-secondary ls-plan-toggle"
                        onClick={() => setShowSettings(!showSettings)}
                        aria-expanded={showSettings}
                    >
                        <Icon name="tune" size={18} />Plan settings
                    </button>
                    {/* Always here (disabled without posted posts), so the buttons don't move once the plan loads */}
                    <button
                        type="button"
                        className="ls-btn ls-btn-secondary"
                        onClick={handleReview}
                        disabled={!hasPosted || !!busy}
                        title={hasPosted
                            ? 'Compare results with targets and adjust the rest of the week'
                            : plan ? 'Mark a post as posted first' : isPastWeek ? 'There was no plan this week' : 'Generate a plan first'}
                    >
                        {busy === 'reviewing' ? <span className="ls-spinner" /> : <Icon name="insights" size={18} />}
                        Check results &amp; adjust plan
                    </button>
                    {/* Weeks that are over keep the plan they had */}
                    {!isPastWeek && (
                        <button
                            type="button"
                            className="ls-btn ls-btn-primary ls-plan-generate"
                            onClick={handleGeneratePlan}
                            disabled={!!busy}
                        >
                            {busy === 'generating' ? <span className="ls-spinner" /> : <Icon name="auto_awesome" size={18} />}
                            {plan ? 'Regenerate plan' : 'Generate plan'}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <Alert severity={error.severity} onClose={() => setError(null)}>{error.message}</Alert>
            )}

            {renderAutomations()}

            <IdeasPanel
                weekStart={weekStart}
                isPastWeek={isPastWeek}
                posts={posts}
                bestTimes={bestTimes}
                onAddPost={addPost}
                onOpenWeek={setWeekStart}
            />

            {showSettings && renderSettings()}

            {loadFailed ? (
                <Alert
                    severity="error"
                    action={<button type="button" className="ls-btn ls-btn-secondary ls-plan-retry" onClick={loadPlan}>Try again</button>}
                >
                    Couldn't load the plan for this week. Check your connection, then try again.
                </Alert>
            ) : !plan && !showSkeleton ? renderEmpty() : (
                <>
                    {lastReview && !showSkeleton && (
                        <section className="ls-card ls-plan-review" aria-labelledby="plan-review-title">
                            <div className="ls-card-header">
                                <h2 className="ls-card-title ls-plan-heading" id="plan-review-title">
                                    <Icon name="insights" size={18} />Latest review
                                </h2>
                                <span className="ls-card-sub">{formatDateTime(lastReview.at)}</span>
                            </div>
                            {lastReview.summary && <p className="ls-plan-review-text">{lastReview.summary}</p>}
                            {lastReview.learnings.length > 0 && (
                                <ul className="ls-plan-review-list">
                                    {lastReview.learnings.map((learning, index) => <li key={index}>{learning}</li>)}
                                </ul>
                            )}
                            {lastReview.changes && <p className="ls-plan-review-text"><b>Plan changes:</b> {lastReview.changes}</p>}
                        </section>
                    )}
                    {renderTotals()}
                    {renderWeek()}
                </>
            )}

            {publishPost && (
                <PublishModal
                    post={publishPost}
                    weekStart={weekStart}
                    linkDestination={[toText(plan?.preferences?.linkDestination), planPreferences.linkDestination.trim()].find(isHttpUrl) || ''}
                    onClose={closePublish}
                    onPublished={(created, meta) => handlePublished(publishPost, created, meta)}
                />
            )}
        </>
    );
};

export default PlanScreen;
